/* eslint-disable max-lines */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getRealtimeErrorKey } from './realtime-errors.ts';
import { REALTIME_WS_URL } from '@utils/constants/app-constants.ts';
import {
  RealtimeConfig,
  RealtimeConnectionState,
  RealtimeMessage,
  RealtimeStats
} from '@utils/types/editors/realtime.ts';

export const useRealtimeWebSocket = () => {
  const { t } = useTranslation();
  const [connectionState, setConnectionState] =
    useState<RealtimeConnectionState>('disconnected');
  const [currentPartial, setCurrentPartial] = useState<string>('');
  const [partialHistory, setPartialHistory] = useState<string[]>([]);
  const [finals, setFinals] = useState<RealtimeMessage[]>([]);
  const [stats, setStats] = useState<RealtimeStats>({
    messagesReceived: 0,
    totalWords: 0,
    connectedTime: 0,
    startTime: null
  });
  const [error, setError] = useState<string | null>(null);

  const websocketRef = useRef<WebSocket | null>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const audioWorkletNodeRef = useRef<AudioWorkletNode | null>(null);
  const currentPartialRef = useRef<string>('');

  const releaseAudio = useCallback(() => {
    if (audioWorkletNodeRef.current) {
      try {
        audioWorkletNodeRef.current.disconnect();
        audioWorkletNodeRef.current.port.close();
      } catch {
        // An already released graph has nothing left to disconnect.
      }
      audioWorkletNodeRef.current = null;
    }
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach(track => track.stop());
      mediaStreamRef.current = null;
    }
    if (audioContextRef.current) {
      if (audioContextRef.current.state !== 'closed')
        void audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }
  }, []);

  const updateConnectedTime = useCallback(() => {
    setStats(prev => {
      if (!prev.startTime) return prev;
      return {
        ...prev,
        connectedTime: Date.now() - prev.startTime
      };
    });
  }, []);

  const startTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    setStats(prev => ({
      ...prev,
      startTime: Date.now(),
      connectedTime: 0
    }));
    timerRef.current = setInterval(updateConnectedTime, 1000);
  }, [updateConnectedTime]);

  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setStats(prev => ({
      ...prev,
      startTime: null
    }));
  }, []);

  const handleMessage = useCallback(
    (event: MessageEvent) => {
      try {
        // Backend (v2 realtime) message shapes:
        //   partial → {"partial": "..."}
        //   final   → {"result": [[word,start,stop,conf]...], "speaker", "text"}
        //   state   → {"state": "listening" | "loading" | "stopped"}
        //   error   → {"error": "..."}
        const data = JSON.parse(event.data) as Record<string, unknown>;

        if ('error' in data) {
          setError(t(getRealtimeErrorKey(data.error)));
          setConnectionState('error');
          return;
        }

        if ('state' in data) {
          if (data.state === 'loading') {
            setConnectionState('loading');
          } else if (data.state === 'listening') {
            setConnectionState('connected');
            startTimer();
          }
          return;
        }

        // Live partial for the in-progress utterance — one mutating line.
        if ('partial' in data) {
          const text = String(data.partial ?? '');
          currentPartialRef.current = text;
          setCurrentPartial(text);
          setStats(prev => ({
            ...prev,
            messagesReceived: prev.messagesReceived + 1
          }));
          return;
        }

        // Finalised utterance — commit it as its own Live Stream entry
        // (accumulating, scrolling list) and add it to the Transcript.
        if ('result' in data || 'text' in data) {
          const words = (
            Array.isArray(data.result)
              ? data.result
              : Array.isArray(data.words)
                ? data.words
                : []
          ) as [string, number, number, number][];

          const text =
            String(data.text ?? '') ||
            words
              .map(w => w[0])
              .join(' ')
              .trim();

          const offset_ms = words.length > 0 ? Number(words[0][1]) || 0 : 0;
          setFinals(prev => [
            ...prev,
            { text, is_final: true, offset_ms, words }
          ]);

          // Commit the corrected final text as a separate live-stream card
          // and clear the active partial so the next utterance starts fresh.
          if (text) {
            setPartialHistory(history => [...history, text]);
          }
          currentPartialRef.current = '';
          setCurrentPartial('');

          setStats(prev => ({
            ...prev,
            messagesReceived: prev.messagesReceived + 1,
            totalWords: prev.totalWords + words.length
          }));
        }
      } catch (err) {
        setError(t('realtime-connection-unavailable'));
        setConnectionState('error');
      }
    },
    [startTimer, t]
  );

  const connect = useCallback(
    async (config: RealtimeConfig) => {
      if (
        websocketRef.current &&
        websocketRef.current.readyState <= WebSocket.OPEN
      ) {
        return;
      }

      // Validate configuration
      if (!config.token) {
        setError(t('realtime-sign-in-again'));
        setConnectionState('error');
        return;
      }

      if (config.audioSource === 'stream' && !config.streamUrl) {
        setError(t('realtime-stream-required'));
        setConnectionState('error');
        return;
      }

      setConnectionState('connecting');
      setError(null);
      // Don't clear partials/finals on reconnect - let user decide when to clear
      currentPartialRef.current = '';
      setCurrentPartial('');
      // Reset stats for new connection
      setStats({
        messagesReceived: 0,
        totalWords: 0,
        connectedTime: 0,
        startTime: null
      });

      try {
        let wsUrl: string;

        if (config.audioSource === 'stream') {
          if (!config.streamUrl) {
            throw new Error('Stream URL is required for stream mode');
          }
          const params = new URLSearchParams({
            url: config.streamUrl
          });
          if (config.language) {
            params.append('language', config.language);
          }
          params.append('type', config.modelType);

          wsUrl = `${REALTIME_WS_URL}/v2/client/${config.token}?${params.toString()}`;
        } else {
          const params = new URLSearchParams({
            token: config.token,
            type: config.modelType
          });
          if (config.language) {
            params.append('language', config.language);
          }

          wsUrl = `${REALTIME_WS_URL}/v2/realtime?${params.toString()}`;
        }

        const ws = new WebSocket(wsUrl);
        websocketRef.current = ws;

        // Add timeout for connection attempt
        const connectionTimeout = setTimeout(() => {
          if (
            websocketRef.current === ws &&
            ws.readyState === WebSocket.CONNECTING
          ) {
            ws.close();
            setError(t('realtime-connection-unavailable'));
            setConnectionState('error');
          }
        }, 15000); // 15 second timeout

        ws.onopen = async () => {
          clearTimeout(connectionTimeout);
          if (websocketRef.current !== ws) return;
          if (config.audioSource === 'microphone') {
            ws.send(JSON.stringify({ action: 'start' }));

            // Start capturing microphone audio using AudioWorklet for raw PCM
            let stream: MediaStream | null = null;
            let audioCtx: AudioContext | null = null;
            let workletNode: AudioWorkletNode | null = null;
            const disposeCapture = () => {
              try {
                workletNode?.disconnect();
                workletNode?.port.close();
              } catch {
                // The active socket may already have released this graph.
              }
              stream?.getTracks().forEach(track => track.stop());
              if (audioCtx && audioCtx.state !== 'closed')
                void audioCtx.close().catch(() => {});
            };
            try {
              stream = await navigator.mediaDevices.getUserMedia({
                audio: {
                  channelCount: 1,
                  sampleRate: 16000,
                  echoCancellation: true,
                  noiseSuppression: true,
                  autoGainControl: true
                }
              });

              if (websocketRef.current !== ws) {
                disposeCapture();
                return;
              }

              mediaStreamRef.current = stream;

              // Create AudioContext at 16kHz to ensure correct sample rate
              // Use webkitAudioContext fallback for older Safari versions
              const AudioContextClass =
                window.AudioContext ||
                (
                  window as unknown as {
                    webkitAudioContext: typeof AudioContext;
                  }
                ).webkitAudioContext;
              audioCtx = new AudioContextClass({ sampleRate: 16000 });

              if (websocketRef.current !== ws) {
                disposeCapture();
                return;
              }

              // Safari requires user interaction to start AudioContext - ensure it's running
              if (audioCtx.state === 'suspended') {
                await audioCtx.resume();
                if (websocketRef.current !== ws) {
                  disposeCapture();
                  return;
                }
              }

              audioContextRef.current = audioCtx;

              // Load the AudioWorklet processor
              await audioCtx.audioWorklet.addModule(
                '/audio-processor-worklet.js'
              );
              if (websocketRef.current !== ws) {
                disposeCapture();
                return;
              }

              // Create source from microphone stream
              const source = audioCtx.createMediaStreamSource(stream);

              // Create AudioWorkletNode for PCM capture
              workletNode = new AudioWorkletNode(
                audioCtx,
                'audio-capture-processor'
              );
              audioWorkletNodeRef.current = workletNode;

              // Handle PCM data from the worklet
              workletNode.port.onmessage = (event: MessageEvent) => {
                if (event.data.type === 'audio') {
                  if (
                    websocketRef.current === ws &&
                    ws.readyState === WebSocket.OPEN
                  ) {
                    ws.send(event.data.data);
                  }
                }
              };

              // Connect the audio graph
              source.connect(workletNode);
              workletNode.connect(audioCtx.destination);
            } catch (err) {
              disposeCapture();
              if (websocketRef.current !== ws) return;
              setError(t('realtime-microphone-permission'));
              setConnectionState('error');
              ws.close();
            }
          }
        };

        ws.onmessage = event => {
          if (websocketRef.current === ws) handleMessage(event);
        };

        ws.onerror = () => {
          clearTimeout(connectionTimeout);
          if (websocketRef.current !== ws) return;
          setError(prev => prev || t('realtime-connection-unavailable'));
          setConnectionState('error');
        };

        ws.onclose = event => {
          clearTimeout(connectionTimeout);
          if (websocketRef.current !== ws) return;
          if (event.code !== 1000) {
            setError(
              prev => prev || t(getRealtimeErrorKey(event.reason, event.code))
            );
          }
          setConnectionState('disconnected');
          stopTimer();
          releaseAudio();
          websocketRef.current = null;
        };
      } catch (err) {
        setError(t('realtime-connection-unavailable'));
        setConnectionState('error');
      }
    },
    [handleMessage, releaseAudio, stopTimer, t]
  );

  const disconnect = useCallback(() => {
    releaseAudio();

    if (websocketRef.current) {
      const ws = websocketRef.current;
      websocketRef.current = null;
      ws.close(1000);
    }

    // Commit the active partial (if any) as a final live-stream entry
    // so nothing in-flight is lost on disconnect.
    if (currentPartialRef.current) {
      const text = currentPartialRef.current;
      currentPartialRef.current = '';
      setPartialHistory(history => [...history, text]);
      setCurrentPartial('');
    }

    stopTimer();
    setError(null);
    setConnectionState('disconnected');
  }, [releaseAudio, stopTimer]);

  const sendAudioData = useCallback(
    (audioData: ArrayBuffer) => {
      if (
        websocketRef.current?.readyState === WebSocket.OPEN &&
        connectionState === 'connected'
      ) {
        websocketRef.current.send(audioData);
      }
    },
    [connectionState]
  );

  const clearPartials = useCallback(() => {
    setCurrentPartial('');
    setPartialHistory([]);
    currentPartialRef.current = '';
  }, []);

  const clearFinals = useCallback(() => {
    setFinals([]);
    setPartialHistory([]);
    setCurrentPartial('');
    currentPartialRef.current = '';
  }, []);

  const clearAll = useCallback(() => {
    setCurrentPartial('');
    currentPartialRef.current = '';
    setPartialHistory([]);
    setFinals([]);
    setStats({
      messagesReceived: 0,
      totalWords: 0,
      connectedTime: 0,
      startTime: null
    });
  }, []);

  const reset = useCallback(() => {
    disconnect();
    clearAll();
  }, [clearAll, disconnect]);

  useEffect(() => {
    return () => {
      if (websocketRef.current) {
        const ws = websocketRef.current;
        websocketRef.current = null;
        ws.close();
      }
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      releaseAudio();
    };
  }, [releaseAudio]);

  return {
    connectionState,
    currentPartial,
    partialHistory,
    finals,
    stats,
    error,
    connect,
    disconnect,
    sendAudioData,
    clearPartials,
    clearFinals,
    clearAll,
    reset
  };
};
